import _ from "lodash";
import spacetime, { Spacetime } from "spacetime";

import {
  APIDataGroup,
  APIDataPoint,
  APIDataPointDesc,
  EAlarmConfig,
  IDataPointCustomConfig,
  IFilter,
  TEquipmentStatus,
} from "@/interfaces";
import { statusColors } from "@/static/constants";

export const toggleTheme = () => {
  const htmlElement = document.querySelector("html");
  if (htmlElement.getAttribute("theme") === "day") {
    htmlElement.setAttribute("theme", "dusk");
  } else {
    htmlElement.setAttribute("theme", "day");
  }

  if (htmlElement.getAttribute("data-obc-theme") === "day") {
    htmlElement.setAttribute("data-obc-theme", "dusk");
  } else {
    htmlElement.setAttribute("data-obc-theme", "day");
  }
};

export function interpolateToDegrees(
  v: number,
  inputMin: number,
  inputMax: number,
  outputMin: number,
  outputMax: number
): number {
  return ((v - inputMin) / (inputMax - inputMin)) * (outputMax - outputMin) + outputMin;
}

export const toHex = (x: number) => {
  const hexValue = Math.round(x).toString(16);
  return hexValue.length === 1 ? "0" + hexValue : hexValue;
};

export const colorToHex = (color: string, alpha?: number) => {
  // Check if the input color is in RGB format
  const values = /\((.*)\)/.exec(color)[1];
  // Extract the individual RGB/RGBA components from the input string
  const [r, g, b, a] = values.split(",").map((a) => Number(a));

  // Convert RGB/RGBA values to hexadecimal

  if (alpha) {
    const alphaHex = toHex(alpha * 255);
    return `#${toHex(r)}${toHex(g)}${toHex(b)}${alphaHex}`;
  }

  if (a) {
    const alphaHex = toHex(a * 255);
    return `#${toHex(r)}${toHex(g)}${toHex(b)}${alphaHex}`;
  }

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export function calculateEmissions(fuelInGallons: number) {
  const GALLON_TO_CUBIC_METER = 0.00378541;
  const CUBIC_METER_TO_METRIC_TON = 1.17;
  const CO2_FACTOR = 3.15;
  const NOX_FACTOR = 50;
  const SOX_FACTOR = 0.03;

  // Convert gallons to cubic meters
  const cubicMeters = fuelInGallons * GALLON_TO_CUBIC_METER;

  // Convert cubic meters to metric tons
  const metricTons = cubicMeters / CUBIC_METER_TO_METRIC_TON;

  // Calculate emissions
  const co2Emissions = metricTons * CO2_FACTOR;
  const noxEmissions = metricTons * NOX_FACTOR;
  const soxEmissions = metricTons * SOX_FACTOR;

  return {
    co2: co2Emissions,
    nox: noxEmissions,
    sox: soxEmissions,
  };
}

// Capitalize the first letter of all words in a string
export const capitalize = (str: string) => {
  try {
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  } catch (error) {
    return "";
  }
};

// Convert a dataGroup data dropdown items id into
// a IFilter object
export const dataGroupIdToFilter = (id: string): IFilter => {
  const idParts = id.split("+").map((id) => Number(id));

  return {
    typeGroupId: idParts[0],
    mainGroupId: idParts[1],
    mainGroupNo: idParts[2],
    subGroupId: idParts[3],
    subGroupNo: idParts[4],
  };
};

export const abbreviate = (str: string) =>
  str
    .split(" ")
    .map((word) => word.charAt(0))
    .join("");

export const getDataByIdKey = <T>(
  id: number,
  arr: T[],
  keyName: keyof T,
  valueName: keyof T
): string => {
  try {
    // @ts-expect-error return type will always be correct
    return arr.find((group) => group[keyName] === id)[valueName];
  } catch (error) {
    console.error(error);
    return "";
  }
};

export function getUrlFromFileName(fileName: string) {
  return `${getApiUrl("")}file?filename=${fileName}`;
}

export function getSubGroupNameById(id: number, arr: APIDataGroup[]) {
  return getDataByIdKey<APIDataGroup>(id, arr, "subGroupId", "subGroupName");
}

export function getMainGroupNameById(id: number, arr: APIDataGroup[]) {
  return getDataByIdKey<APIDataGroup>(id, arr, "mainGroupId", "mainGroupName");
}

export function getTypeGroupNameById(id: number, arr: APIDataGroup[]) {
  return getDataByIdKey<APIDataGroup>(id, arr, "typeGroupId", "typeGroupName");
}

export function getAPIDataGroupName(dataPointDesc: APIDataPointDesc) {
  let groupName = "";

  if (dataPointDesc.typeGroupName && dataPointDesc.typeGroupId > 0)
    groupName += abbreviate(dataPointDesc.typeGroupName).toLocaleUpperCase();

  if (dataPointDesc.mainGroupId && dataPointDesc.mainGroupId > 0)
    groupName += abbreviate(dataPointDesc.mainGroupName).toLocaleUpperCase();

  if (dataPointDesc.mainGroupNo) groupName += dataPointDesc.mainGroupNo;

  if (dataPointDesc.subGroupId && dataPointDesc.subGroupId > 0)
    groupName += abbreviate(dataPointDesc.subGroupName).toLocaleUpperCase();

  if (dataPointDesc.subGroupNo) groupName += dataPointDesc.subGroupNo;

  return groupName;
}

export function getAPIDataPointName(dataPoint: APIDataPoint, arr: APIDataGroup[]) {
  return (
    (dataPoint.typeGroupId && dataPoint.typeGroupId > 0
      ? abbreviate(getTypeGroupNameById(dataPoint.typeGroupId, arr)).toLocaleUpperCase()
      : "") +
    (dataPoint.mainGroupId && dataPoint.mainGroupId > 0
      ? abbreviate(getMainGroupNameById(dataPoint.mainGroupId, arr)).toLocaleUpperCase()
      : "") +
    (dataPoint.mainGroupNo ?? "") +
    (dataPoint.subGroupId && dataPoint.subGroupId > 0
      ? abbreviate(getSubGroupNameById(dataPoint.subGroupId, arr)).toLocaleUpperCase()
      : "") +
    (dataPoint.subGroupNo ?? "") +
    " " +
    capitalize(dataPoint.measPointName)
  );
}

export function dataPointShortNameFromDataPointDesc(dataPointDesc: APIDataPointDesc) {
  const { mainGroupName, mainGroupNo, subGroupNo, measPointName, subGroupName, typeGroupName } =
    dataPointDesc;
  return (
    (typeGroupName ? abbreviate(typeGroupName).toLocaleUpperCase() : "") +
    (mainGroupName ? abbreviate(mainGroupName).toLocaleUpperCase() : "") +
    (mainGroupNo ?? "") +
    (subGroupName ? abbreviate(subGroupName).toLocaleUpperCase() : "") +
    (subGroupNo ?? "") +
    " " +
    capitalize(measPointName)
  );
}

export function dataPointFullNameFromDataPointDesc(dataPointDesc: APIDataPointDesc) {
  try {
    const {
      mainGroupName = "",
      mainGroupNo,
      subGroupNo,
      measPointName = "",
      subGroupName = "",
      typeGroupName = "",
    } = dataPointDesc;
    return `${capitalize(typeGroupName)} ${capitalize(mainGroupName)} ${
      mainGroupNo ?? ""
    } ${capitalize(subGroupName)} ${subGroupNo ?? ""} ${capitalize(measPointName)}`;
  } catch (error) {
    return "An error occured";
  }
}

export function randomNumberArray(x = 5) {
  return Array.from({ length: x }, () => Math.floor(Math.random() * 100));
}

export const thresholdTypesArray: Array<"high high" | "high" | "low" | "low low"> = [
  "high high",
  "high",
  "low",
  "low low",
];

export const alarmTypeToConfigId = (val: "high high" | "high" | "low" | "low low") => {
  switch (val) {
    case "high":
      return EAlarmConfig.High;
    case "high high":
      return EAlarmConfig.HighHigh;
    case "low":
      return EAlarmConfig.Low;
    case "low low":
      return EAlarmConfig.LowLow;
    default:
      return EAlarmConfig.High;
  }
};

export const alarmConfig = {
  high: {
    typeId: 1,
    configId: 1,
    operator: ">",
    runConId: -1,
    typeName: "high",
    alarmName: "high",
    severityId: 2,
    severitySeq: 2,
  },
  "high high": {
    typeId: 1,
    configId: 5,
    operator: ">",
    runConId: -1,
    typeName: "high",
    alarmName: "high high",
    severityId: 3,
    severitySeq: 3,
  },
  low: {
    typeId: 2,
    configId: 3,
    operator: "<",
    runConId: -1,
    typeName: "low",
    alarmName: "low",
    severityId: 2,
    severitySeq: 2,
  },
  "low low": {
    typeId: 2,
    configId: 6,
    operator: "<",
    runConId: -1,
    typeName: "low",
    alarmName: "low low",
    severityId: 3,
    severitySeq: 3,
  },
};

export function generateRandomId() {
  return Math.random()
    .toString(36)
    .replace(/[^a-z]+/g, "")
    .slice(0, 5);
}

export function getApiUrl(path: string): string {
  const apiUrlLS = localStorage.getItem("API_URL");
  if (apiUrlLS) {
    return apiUrlLS.endsWith("/") ? apiUrlLS : `${apiUrlLS}/`;
  }
  const apiUrlENV = import.meta.env.VITE_API_URL;
  if (apiUrlENV) {
    return apiUrlENV.endsWith("/") ? apiUrlENV : `${apiUrlENV}/`;
  }
  return `${window.location.origin}${path.endsWith("/") ? path : `${path}/`}`;
}

export function getColorFromStatus(status: TEquipmentStatus) {
  switch (status) {
    case "warning":
      return statusColors.warning;
    case "observation":
      return statusColors.observation;
    case "danger":
      return statusColors.danger;
    case "ok":
      return statusColors.ok;
    case "caution":
      return statusColors.caution;
  }
}

export function getAbbreviationFromStatus(status: TEquipmentStatus) {
  switch (status) {
    case "ok":
      return "OK";

    case "warning":
    case "observation":
    case "danger":
    case "caution":
      return status[0].toUpperCase();
  }
}

export function getUtcTimeStr(isoTimestamp: string | Spacetime): string {
  if (typeof isoTimestamp === "string") {
    return spacetime(isoTimestamp).format(
      "{date-pad} {month-short} {year}, {hour-24-pad}:{minute-pad}:{second-pad}"
    );
  } else {
    return isoTimestamp.format(
      "{date-pad} {month-short} {year}, {hour-24-pad}:{minute-pad}:{second-pad}"
    );
  }
}

// Label Alias logic
export function onLabelDblClick(el: HTMLSpanElement) {
  el.setAttribute("contenteditable", "true");
  el.focus();
}

export function onLabelUnfocus(el: HTMLSpanElement, dp: IDataPointCustomConfig) {
  el.setAttribute("contenteditable", "false");
  dp.alias = el.innerText;
}

export function toUtcTimeStr(
  month: string,
  day: string,
  hour: string,
  minute: string,
  second?: string
): string {
  const date = new Date(
    Date.UTC(
      new Date().getUTCFullYear(),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hour),
      parseInt(minute),
      parseInt(!_.isNil(second) ? second : "0")
    )
  );

  return spacetime(date, "Etc/Greenwich").format(
    "{date-pad} {month-short} {year}, {hour-24-pad}:{minute-pad}:{second-pad}"
  );
}
